import React from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { IspPage } from './ispLayout';
import { useIspGate } from './useIspGate';
import { ROW_ACTION_GAP } from './ispSettingsUI';
import type { PlanDraft } from './IspPlanForm';
import {
   getIspPlans,
   createIspPlan,
   updateIspPlan,
   deleteIspPlan,
   pushIspPlanProfile,
   createStarterPlans,
   getIspServers,
   type IspPlanRow,
} from '@/api/isp';

/**
 * ISP Business — Internet plans.
 *
 * Its own screen since 2026-08-31. Ray: *"i change my mind, the internet plans
 * lets move it in the main menu since this is also important."* They had been a
 * tab in Settings on the theory that they are configuration set once. They are
 * not — they are the price list, edited whenever the business changes what it
 * sells, and burying that two clicks deep under a gear icon was wrong.
 *
 * Every row says whether it is LINKED to a MikroTik, because an unlinked plan
 * enforces nothing: it is a price with no router behind it, and that is
 * invisible in a row showing a name and a peso figure.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;

const IspPlans: React.FC = () => {
   const qc = useQueryClient();
   const { handleShow, handleClose } = useUIStore();
   const { gate } = useIspGate();

   const { data: plans = [], isLoading } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
   });
   const { data: servers = [] } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
      staleTime: 30_000,
   });

   const serverNames = (ids: string[]) =>
      ids
         .map((id) => servers.find((sv) => sv.id === id)?.name)
         .filter(Boolean)
         .join(', ');

   const savePlan = useMutation({
      mutationFn: (v: { id: string | null; draft: PlanDraft }) =>
         v.id
            ? updateIspPlan(v.id, v.draft as never)
            : createIspPlan(v.draft as never),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-plans'] });
         qc.invalidateQueries({ queryKey: ['isp-readiness'] });
         handleClose();
         // The speed goes to the router as part of saving, so the toast reports
         // what actually reached the hardware rather than just "Saved."
         if (r.sync.ok) toast.success(r.sync.message || 'Saved.');
         else toast.warning(r.sync.message);
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not save the plan.')),
   });

   const removePlan = useMutation({
      mutationFn: (id: string) => deleteIspPlan(id),
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-plans'] });
         handleClose();
         toast.success('Plan removed.');
      },
      // A 409 names how many subscribers are still on it. Shown verbatim.
      onError: (e: unknown) => toast.error(msg(e, 'Could not remove the plan.')),
   });

   const pushProfile = useMutation({
      mutationFn: (id: string) => pushIspPlanProfile(id),
      onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
      onError: (e: unknown) => toast.error(msg(e, 'Could not write to the router.')),
   });

   const seed = useMutation({
      mutationFn: createStarterPlans,
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-plans'] });
         qc.invalidateQueries({ queryKey: ['isp-readiness'] });
         toast.success('Three starter plans created. Edit them to your prices.');
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not create them.')),
   });

   const openPlan = (existing: IspPlanRow | null) =>
      handleShow({
         template: 'isp-plan-form',
         title: existing ? `Edit — ${existing.name}` : 'New plan',
         windowType: 'desktop',
         data: {
            existing,
            onCancel: handleClose,
            onSave: (draft: PlanDraft) =>
               savePlan.mutate({ id: existing?.id ?? null, draft }),
         },
      });

   /** Deleting asks in a modal, the same as everywhere else in the panel. */
   const confirmRemove = (p: IspPlanRow) =>
      handleShow({
         template: 'isp-confirm',
         title: '',
         windowType: 'desktop',
         data: {
            title: `Delete ${p.name}?`,
            message:
               'Old invoices keep naming this plan, so nothing already billed changes. New subscribers just will not be able to pick it.',
            confirmLabel: 'Yes, delete',
            onConfirm: () => removePlan.mutateAsync(p.id),
            onCancel: handleClose,
         },
      });

   return (
      <IspPage>
         <TopRow>
            <Title>Internet plans</Title>
            <Primary
               type="button"
               onClick={() => gate('server', () => openPlan(null))}
            >
               + Add plan
            </Primary>
         </TopRow>
         <Note>
            What you sell, and the speed limit each one puts on the router. Grace
            is set per plan — a business line and a home line do not get the same
            patience. <strong>Send speed to router</strong> writes the limit onto
            the MikroTik; until you do, a plan is only a price and the router
            enforces nothing.
         </Note>

         {isLoading && <SkeletonLines lines={3} />}

         {!isLoading && plans.length === 0 && (
            <EmptyState>
               <EmptyBig>No plans yet</EmptyBig>
               <EmptySub>
                  Start from three ordinary tiers and change the prices to yours,
                  or write your own from scratch.
               </EmptySub>
               <Row>
                  <Primary
                     type="button"
                     disabled={seed.isPending}
                     onClick={() => seed.mutate()}
                  >
                     {seed.isPending ? 'Creating…' : 'Create three starter plans'}
                  </Primary>
                  <Ghost
                     type="button"
                     onClick={() => gate('server', () => openPlan(null))}
                  >
                     Write my own
                  </Ghost>
               </Row>
            </EmptyState>
         )}

         {plans.map((p) => (
            <Card key={p.id}>
               <CardMain>
                  <CardTitle>
                     {p.name} — {peso(p.price)}/mo
                  </CardTitle>
                  <CardSub>
                     {[
                        p.speedLabel,
                        p.bandwidth,
                        p.category,
                        p.routerProfile,
                        `${p.graceDays}-day grace`,
                     ]
                        .filter(Boolean)
                        .join(' · ')}
                  </CardSub>
                  {/* Linked, enforced, or failed — three states, not two. A
                      plan that names a router the router has never heard of
                      would otherwise read as finished. */}
                  <LinkChip
                     $tone={
                        p.servers.length === 0
                           ? 'none'
                           : p.profileSyncError
                             ? 'bad'
                             : p.profileSyncedAt
                               ? 'ok'
                               : 'none'
                     }
                  >
                     {p.servers.length === 0
                        ? 'Not linked to a MikroTik'
                        : p.profileSyncError
                          ? `Speed did not reach ${serverNames(p.servers)}`
                          : p.profileSyncedAt
                            ? `Enforced on ${serverNames(p.servers)}`
                            : `On ${serverNames(p.servers)} — speed not sent yet`}
                  </LinkChip>
               </CardMain>
               <CardActions>
                  {/* Only once the plan knows WHICH router and WHICH profile —
                      writing to somebody's live PPPoE server before they have
                      said where is the guest promise the setup script makes. */}
                  {/* 🔴 NO "SEND TO ROUTER" BUTTON. Ray, 2026-08-31: "that
                      button create on router is not telling the operator to
                      click me." Saving does it. This appears ONLY when the write
                      actually failed — a permanent button asking somebody to
                      finish a job SmartFi could finish itself is not a feature,
                      but a retry after a router was down is. */}
                  {p.servers.length > 0 && p.profileSyncError && (
                     <LinkBtn
                        type="button"
                        disabled={pushProfile.isPending}
                        onClick={() => pushProfile.mutate(p.id)}
                        title={p.profileSyncError}
                     >
                        {pushProfile.isPending ? 'Sending…' : 'Retry sending speed'}
                     </LinkBtn>
                  )}
                  <LinkBtn type="button" onClick={() => openPlan(p)}>
                     Edit
                  </LinkBtn>
                  <DangerBtn type="button" onClick={() => confirmRemove(p)}>
                     Delete
                  </DangerBtn>
               </CardActions>
            </Card>
         ))}
      </IspPage>
   );
};

export default IspPlans;

const msg = (e: unknown, fallback: string) =>
   (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
   fallback;

const TopRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const Title = styled.h1`
   margin: 0;
   font-size: 20px;
`;

const Note = styled.p`
   margin: 0;
   font-size: 12.5px;
   line-height: 1.6;
   color: ${colors.GRAY5};
   max-width: 70ch;
`;

const Card = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 14px;
   padding: 14px 16px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   flex-wrap: wrap;
`;

const CardMain = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;
`;

const CardTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const CardSub = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;

/** Whether the plan has a router behind it at all. */
const LinkChip = styled.span<{ $tone: 'ok' | 'bad' | 'none' }>`
   align-self: flex-start;
   margin-top: 2px;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 10.5px;
   font-weight: 700;
   background: ${({ $tone }) =>
      $tone === 'ok'
         ? `${colors.SUCCESS}18`
         : $tone === 'bad'
           ? `${colors.DANGER}18`
           : `${colors.ORANGE}18`};
   color: ${({ $tone }) =>
      $tone === 'ok'
         ? colors.SUCCESS
         : $tone === 'bad'
           ? colors.DANGER
           : colors.ORANGE};
`;

// A card, not a table row, so the card itself pushes these right. The gap
// matches every other action row — see ROW_ACTION_GAP in ispSettingsUI.
const CardActions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: ${ROW_ACTION_GAP};
   align-items: center;
   flex-wrap: wrap;
`;

const Row = styled.div`
   display: flex;
   gap: 8px;
   flex-wrap: wrap;
   justify-content: center;
`;

const Primary = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const Ghost = styled.button`
   background: transparent;
   color: inherit;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

const LinkBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const DangerBtn = styled.button`
   background: transparent;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
`;

const EmptyRow = styled.p`
   margin: 0;
   padding: 18px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const EmptyState = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 40px 22px;
   text-align: center;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;

const EmptyBig = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const EmptySub = styled.p`
   margin: 0 0 6px;
   max-width: 440px;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;
